"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Instagram, X, Send, Clock, AlertCircle, Facebook } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/use-toast";
import {
  useInstagramAccounts,
  useInstagramActions,
  type InstagramAccount,
} from "@/lib/hooks/useInstagram";
import { useLanguageNavigate } from "@/lib/hooks/useLanguageNavigate";

interface InstagramPublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  videoTitle: string;
  onPublished?: () => void;
}

export function InstagramPublishDialog({
  open,
  onOpenChange,
  jobId,
  videoTitle,
  onPublished,
}: InstagramPublishDialogProps) {
  const { t } = useTranslation();
  const navigate = useLanguageNavigate();
  const { data: accountsData, isLoading: accountsLoading } = useInstagramAccounts();
  const actions = useInstagramActions();

  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [postType, setPostType] = useState<"reel" | "feed">("reel");
  const [caption, setCaption] = useState("");
  const [hashtagInput, setHashtagInput] = useState("");
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [mirrorToFacebook, setMirrorToFacebook] = useState(false);
  const [fbPostType, setFbPostType] = useState<"reel" | "video">("reel");
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const accounts = accountsData?.accounts || [];
  const maxCaption = 2200;
  const maxHashtags = 30;

  // Auto-select first account
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(accounts[0]!.id);
    }
  }, [accounts, selectedAccountId]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setCaption("");
      setHashtags([]);
      setHashtagInput("");
      setPostType("reel");
      setIsScheduled(false);
      setScheduledAt("");
      setIsPublishing(false);
      setMirrorToFacebook(false);
      setFbPostType("reel");
    }
  }, [open]);

  const addHashtag = () => {
    const tag = hashtagInput.trim().replace(/^#/, "");
    if (!tag) return;
    if (hashtags.length >= maxHashtags) return;
    if (hashtags.includes(tag)) return;
    setHashtags([...hashtags, tag]);
    setHashtagInput("");
  };

  const removeHashtag = (tag: string) => {
    setHashtags(hashtags.filter((h) => h !== tag));
  };

  const handleHashtagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addHashtag();
    }
  };

  const handlePublish = async () => {
    if (!selectedAccountId || !jobId) return;

    setIsPublishing(true);
    try {
      await actions.publish({
        jobId,
        accountId: selectedAccountId,
        postType,
        caption,
        hashtags,
        ...(isScheduled && scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}),
        ...(mirrorToFacebook ? { mirrorToFacebook: true, fbPostType } : {}),
      });

      toast({
        title: isScheduled ? t("instagram.scheduled") : t("instagram.publishing"),
        description: isScheduled ? t("instagram.scheduledDesc") : t("instagram.publishingDesc"),
      });

      onPublished?.();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t("instagram.publishError"),
        description: error instanceof Error ? error.message : t("errors.defaultMessage"),
        variant: "destructive",
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);

  // Minimum schedule time: 10 minutes from now
  const minScheduleTime = new Date(Date.now() + 10 * 60 * 1000)
    .toISOString()
    .slice(0, 16);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Instagram className="w-5 h-5" />
            {t("instagram.publishToInstagram")}
          </DialogTitle>
          <DialogDescription>
            {t("instagram.publishDialogDesc", { title: videoTitle })}
          </DialogDescription>
        </DialogHeader>

        {accountsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <AlertCircle className="w-10 h-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground max-w-[220px]">
              {t("instagram.noAccountsConnect")}
            </p>
            <Button
              size="sm"
              className="gap-2 bg-linear-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              onClick={() => {
                onOpenChange(false);
                navigate("/account#instagram");
              }}
            >
              <Instagram className="w-3.5 h-3.5" />
              {t("instagram.connectAccount")}
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Account selector */}
            <div className="space-y-2">
              <Label>{t("instagram.account")}</Label>
              <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("instagram.selectAccount")} />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      <div className="flex items-center gap-2">
                        {account.profilePictureUrl && !imageErrors[account.id] ? (
                          <img
                            src={account.profilePictureUrl}
                            alt=""
                            onError={() => setImageErrors((prev) => ({ ...prev, [account.id]: true }))}
                            className="w-5 h-5 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-linear-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                            <Instagram className="w-3 h-3 text-white" />
                          </div>
                        )}
                        @{account.instagramUsername}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Post type */}
            <div className="space-y-2">
              <Label>{t("instagram.postType")}</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={postType === "reel" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPostType("reel")}
                  className="flex-1"
                >
                  {t("instagram.reel")}
                </Button>
                <Button
                  type="button"
                  variant={postType === "feed" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPostType("feed")}
                  className="flex-1"
                >
                  {t("instagram.feedVideo")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {postType === "reel" ? t("instagram.reelDesc") : t("instagram.feedVideoDesc")}
              </p>
            </div>

            {/* Caption */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("instagram.caption")}</Label>
                <span className="text-xs text-muted-foreground">
                  {caption.length}/{maxCaption}
                </span>
              </div>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, maxCaption))}
                placeholder={t("instagram.captionPlaceholder")}
                rows={3}
                className="resize-none"
              />
            </div>

            {/* Hashtags */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("instagram.hashtags")}</Label>
                <span className="text-xs text-muted-foreground">
                  {hashtags.length}/{maxHashtags}
                </span>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  onKeyDown={handleHashtagKeyDown}
                  placeholder={t("instagram.hashtagPlaceholder")}
                  className="flex-1 px-3 py-2 text-sm border rounded-md bg-background"
                  disabled={hashtags.length >= maxHashtags}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addHashtag}
                  disabled={!hashtagInput.trim() || hashtags.length >= maxHashtags}
                >
                  {t("instagram.addHashtag")}
                </Button>
              </div>
              {hashtags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {hashtags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="gap-1 cursor-pointer hover:bg-destructive/10"
                      onClick={() => removeHashtag(tag)}
                    >
                      #{tag}
                      <X className="w-3 h-3" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Schedule toggle */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="schedule-toggle"
                  checked={isScheduled}
                  onChange={(e) => setIsScheduled(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="schedule-toggle" className="cursor-pointer">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {t("instagram.schedulePost")}
                  </div>
                </Label>
              </div>
              {isScheduled && (
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  min={minScheduleTime}
                  className="w-full px-3 py-2 text-sm border rounded-md bg-background"
                />
              )}
            </div>

            {/* Facebook cross-post toggle */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="fb-toggle"
                  checked={mirrorToFacebook}
                  onChange={(e) => setMirrorToFacebook(e.target.checked)}
                  className="rounded border-gray-300"
                  disabled={!selectedAccount?.pageName}
                />
                <Label htmlFor="fb-toggle" className="cursor-pointer">
                  <div className="flex items-center gap-1.5">
                    <Facebook className="w-3.5 h-3.5" />
                    {t("instagram.facebook.alsoPostToFacebook")}
                  </div>
                </Label>
              </div>
              {!selectedAccount?.pageName && selectedAccountId && (
                <p className="text-xs text-amber-600">
                  {t("instagram.facebook.reconnectRequired")}
                </p>
              )}
              {selectedAccount?.pageName && (
                <p className="text-xs text-muted-foreground">
                  {t("instagram.facebook.alsoPostToFacebookDesc")} — {selectedAccount.pageName}
                </p>
              )}
              {mirrorToFacebook && (
                <div className="space-y-2 pl-6 border-l-2 border-blue-200">
                  <Label>{t("instagram.facebook.fbPostType")}</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={fbPostType === "reel" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFbPostType("reel")}
                      className="flex-1"
                    >
                      {t("instagram.facebook.fbReel")}
                    </Button>
                    <Button
                      type="button"
                      variant={fbPostType === "video" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFbPostType("video")}
                      className="flex-1"
                    >
                      {t("instagram.facebook.fbPageVideo")}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {fbPostType === "reel"
                      ? t("instagram.facebook.fbReelDesc")
                      : t("instagram.facebook.fbPageVideoDesc")}
                  </p>
                </div>
              )}
            </div>

            {/* Publish button */}
            <Button
              onClick={handlePublish}
              disabled={isPublishing || !selectedAccountId}
              className="w-full bg-linear-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white gap-2"
            >
              {isPublishing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isScheduled ? (
                <Clock className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {isPublishing
                ? t("instagram.publishingAction")
                : isScheduled
                  ? t("instagram.scheduleAction")
                  : t("instagram.publishAction")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
